/**
 * TMG cart drawer controller (Empire theme).
 *
 * Standalone, no theme-framework dependencies. Intercepts every add-to-cart
 * form (product page, collection cards, home cards, quick-add), adds via the
 * Cart AJAX API, then re-renders and opens the drawer. Also turns the header
 * cart icons into drawer triggers, and handles quantity / remove inside the
 * drawer.
 *
 * The drawer markup is produced by sections/cart-drawer.liquid + the
 * tmg-cart-drawer snippet and re-fetched through the Section Rendering API.
 */
(function () {
  // Guard on a dedicated flag, not on window.TMGCartDrawer: an element id could
  // otherwise clobber that name via named window access and bail us out.
  if (window.__TMGCartDrawerLoaded) return;
  window.__TMGCartDrawerLoaded = true;

  var SECTION_ID = 'cart-drawer';
  var DRAWER_SELECTOR = 'tmg-cart-drawer';
  // Both selectors are owned by the bootstrap in layout/theme.liquid, which
  // needs them before this deferred script runs.
  var ADD_FORM_SELECTOR = window.TMG_CART_ADD_FORM;
  var OPEN_TRIGGER_SELECTOR = window.TMG_CART_TRIGGER;

  var routes = (window.Theme && window.Theme.routes) || window.routes || {};
  var CART_ADD_URL = (routes.cart_add_url || '/cart/add') + '.js';
  var CART_CHANGE_URL = (routes.cart_change_url || '/cart/change') + '.js';
  var closeTimer = null;
  var closePanel = null;
  var closeTransitionHandler = null;

  /* ---------------------------------------------------------------- utils */

  function getDrawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function getDrawerMessage(name, fallback) {
    var drawer = getDrawer();
    return (drawer && drawer.dataset[name]) || fallback;
  }

  function parseSectionHTML(html) {
    if (!html) return null;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(DRAWER_SELECTOR);
  }

  /* The header badge draws its number from CSS (content: attr(...)), so keeping
     it current is one attribute write — no innerHTML, no reflow past the badge.
     The re-rendered drawer already carries the same exclusion-aware count the
     header computes in Liquid (delivery surcharge and Premium Shipping
     Protection are left out of both), so nothing here needs its own /cart.js. */
  function syncHeaderCount(count) {
    var value = Number(count) > 0 ? String(count) : '';
    var badges = document.querySelectorAll('.site-header-cart--count[data-header-cart-count]');

    for (var i = 0; i < badges.length; i++) {
      // Skip identical writes so an unchanged cart never invalidates style.
      if (badges[i].getAttribute('data-header-cart-count') === value) continue;
      badges[i].setAttribute('data-header-cart-count', value);
      badges[i].classList.toggle('visible', value !== '');
    }
  }

  /* Replace the drawer's inner content while keeping the outer element (and its
     open state + delegated listeners) stable. */
  function applySection(html) {
    var drawer = getDrawer();
    var fresh = parseSectionHTML(html);
    if (!fresh) return false;

    /* Ahead of the drawer guard: on a page without the drawer the count is
       still worth refreshing, and every cart mutation reaches this function. */
    syncHeaderCount(fresh.dataset.itemCount);

    if (!drawer) return false;

    drawer.innerHTML = fresh.innerHTML;
    drawer.dataset.itemCount = fresh.dataset.itemCount || '0';
    drawer.classList.toggle('tmg-cd--empty', fresh.classList.contains('tmg-cd--empty'));
    return true;
  }

  /* Deliberately no Accept: application/json header. Product URLs content
     negotiate on it and answer with the product JSON, which shadows the Section
     Rendering API — every call from a product page came back as {product: ...}
     and the drawer never refreshed. The sections param already returns JSON. */
  function fetchDrawerSection() {
    var url = window.location.pathname + '?sections=' + SECTION_ID + '&_=' + Date.now();
    return fetch(url, { cache: 'no-store' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) { return data[SECTION_ID]; });
  }

  function syncShippingProtection() {
    if (!window.shippingProtection || typeof window.shippingProtection.updateProtection !== 'function') {
      return Promise.resolve();
    }

    return window.shippingProtection.updateProtection().catch(function () {});
  }

  /* ---------------------------------------------------------------- open/close */

  function lockPageScroll() {
    var body = document.body;
    if (!body || body.classList.contains('tmg-cd-open')) return;

    var scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    var bodyPaddingRight = parseFloat(window.getComputedStyle(body).paddingRight) || 0;

    // Preserve the page's content width when overflow:hidden removes the
    // desktop scrollbar, otherwise the centered layout shifts horizontally.
    body.style.setProperty(
      '--tmg-cd-locked-padding-right',
      (bodyPaddingRight + scrollbarWidth) + 'px'
    );
    body.classList.add('tmg-cd-open');
  }

  function unlockPageScroll() {
    var body = document.body;
    if (!body) return;

    body.classList.remove('tmg-cd-open');
    body.style.removeProperty('--tmg-cd-locked-padding-right');
  }

  function cancelCloseCleanup() {
    if (closeTimer !== null) {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (closePanel && closeTransitionHandler) {
      closePanel.removeEventListener('transitionend', closeTransitionHandler);
    }

    closePanel = null;
    closeTransitionHandler = null;
  }

  function finishClose(drawer) {
    cancelCloseCleanup();

    // A new open may have interrupted the closing transition.
    if (!drawer || drawer.classList.contains('is-active')) return;

    unlockPageScroll();

    var trigger = drawer._tmgTrigger;
    if (trigger && typeof trigger.focus === 'function') {
      try { trigger.focus(); } catch (e) {}
    }
    drawer._tmgTrigger = null;
  }

  function openDrawer(trigger) {
    var drawer = getDrawer();
    if (!drawer) return;

    cancelCloseCleanup();
    if (trigger) drawer._tmgTrigger = trigger;

    // Force a reflow so the transition runs even right after innerHTML swaps.
    void drawer.offsetWidth;
    drawer.classList.add('is-active');
    lockPageScroll();

    var panel = drawer.querySelector('[data-tmg-cd-panel]');
    if (panel) {
      try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    }
  }

  function closeDrawer() {
    var drawer = getDrawer();
    if (!drawer) return;

    cancelCloseCleanup();
    drawer.classList.remove('is-active');

    var panel = drawer.querySelector('[data-tmg-cd-panel]');
    if (!panel) {
      finishClose(drawer);
      return;
    }

    // Keep tmg-cd-open until the panel has fully slid out. Removing it here
    // exposes product sticky bars underneath during the 200ms close animation.
    closePanel = panel;
    closeTransitionHandler = function (event) {
      if (event.target !== panel || event.propertyName !== 'transform') return;
      finishClose(drawer);
    };
    panel.addEventListener('transitionend', closeTransitionHandler);

    // Fallback for browsers that do not emit transitionend (for example when
    // the tab loses visibility during the animation).
    closeTimer = window.setTimeout(function () {
      finishClose(drawer);
    }, 400);
  }

  /* ---------------------------------------------------------------- add to cart */

  function showFormError(form, message) {
    if (!form) return;

    var prev = form.querySelector('[data-tmg-cart-error]');
    if (prev) prev.remove();

    var el = document.createElement('div');
    el.setAttribute('role', 'alert');
    el.setAttribute('data-tmg-cart-error', '');
    el.className = 'tmg-cd-form-error';
    el.style.cssText = 'margin:8px 0;color:#8a1f11;font-size:13px;line-height:1.4;';
    el.textContent = message || getDrawerMessage('addError', 'Unable to add to cart.');
    form.prepend(el);
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    if (loading) {
      button.setAttribute('aria-disabled', 'true');
      button.classList.add('loading', 'processing');
    } else {
      button.removeAttribute('aria-disabled');
      button.classList.remove('loading', 'processing');
    }
  }

  function addFromForm(form, submitButton) {
    if (form.dataset.tmgSubmitting === 'true') return;
    form.dataset.tmgSubmitting = 'true';

    var prev = form.querySelector('[data-tmg-cart-error]');
    if (prev) prev.remove();
    setButtonLoading(submitButton, true);

    var formData = new FormData(form);
    formData.set('sections', SECTION_ID);
    formData.set('sections_url', window.location.pathname);

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
      cache: 'no-store'
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) {
            throw new Error(data.description || data.message || getDrawerMessage('addError', 'Unable to add to cart.'));
          }
          return data;
        });
      })
      .then(function (data) {
        if (data.sections && data.sections[SECTION_ID]) {
          applySection(data.sections[SECTION_ID]);
          openDrawer(submitButton);
        } else {
          // Fallback: fetch the section separately.
          return fetchDrawerSection().then(function (html) {
            applySection(html);
            openDrawer(submitButton);
          });
        }
      })
      .then(syncShippingProtection)
      .catch(function (error) {
        showFormError(form, error.message || getDrawerMessage('addError', 'Unable to add to cart.'));
      })
      .finally(function () {
        delete form.dataset.tmgSubmitting;
        setButtonLoading(submitButton, false);
      });
  }

  /* Custom bundle builders in a few CA product templates add multiple items
     directly through /cart/add.js. Let them keep their payload logic, then use
     this hook to refresh and open the same drawer instead of navigating away. */
  function afterExternalAdd(trigger, sectionHTML) {
    var sectionRequest = sectionHTML ? Promise.resolve(sectionHTML) : fetchDrawerSection();

    return sectionRequest
      .then(function (html) {
        if (!applySection(html)) {
          throw new Error(getDrawerMessage('updateError', 'Unable to update cart.'));
        }
        openDrawer(trigger);
      })
      .then(syncShippingProtection);
  }

  /* ---------------------------------------------------------------- qty / remove */

  function changeLine(key, quantity) {
    var drawer = getDrawer();
    if (drawer) drawer.classList.add('tmg-cd--busy');

    return fetch(CART_CHANGE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity, sections: SECTION_ID, sections_url: window.location.pathname }),
      cache: 'no-store'
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            throw new Error(data.message || getDrawerMessage('updateError', 'Unable to update cart.'));
          }
          return data;
        });
      })
      .then(function (data) {
        if (data.sections && data.sections[SECTION_ID]) {
          applySection(data.sections[SECTION_ID]);
        } else {
          return fetchDrawerSection().then(applySection);
        }
      })
      .catch(function () {
        return fetchDrawerSection().then(applySection);
      })
      .then(syncShippingProtection)
      .finally(function () {
        if (drawer) drawer.classList.remove('tmg-cd--busy');
      });
  }

  function getLineItem(target) {
    return target.closest('[data-tmg-cd-item]');
  }

  function handleQtyStep(button) {
    var item = getLineItem(button);
    if (!item) return;
    var input = item.querySelector('[data-tmg-cd-qty-input]');
    if (!input) return;

    var step = parseInt(input.getAttribute('step'), 10) || 1;
    var current = parseInt(input.value, 10) || 0;
    var next = button.getAttribute('data-tmg-cd-qty') === 'plus' ? current + step : current - step;
    if (next < 0) next = 0;

    input.value = next;
    item.classList.add('is-busy');
    changeLine(item.getAttribute('data-key'), next);
  }

  function handleQtyInput(input) {
    var item = getLineItem(input);
    if (!item) return;
    var value = parseInt(input.value, 10);
    if (isNaN(value) || value < 0) value = 0;
    item.classList.add('is-busy');
    changeLine(item.getAttribute('data-key'), value);
  }

  function handleRemove(button) {
    var item = getLineItem(button);
    if (!item) return;
    item.classList.add('is-busy');
    changeLine(item.getAttribute('data-key'), 0);
  }

  /* ---------------------------------------------------------------- listeners */

  // Intercept add-to-cart submissions (capture phase, so we win over the theme).
  document.addEventListener(
    'submit',
    function (event) {
      if (!ADD_FORM_SELECTOR) return;
      var form = event.target;
      if (!form || !form.matches || !form.matches(ADD_FORM_SELECTOR)) return;

      var idField = form.querySelector('[name="id"]');
      if (!idField) return; // Not a real add-to-cart form.

      var submitter = event.submitter || form.querySelector('[type="submit"]');

      // Respect disabled / unavailable add buttons and explicit opt-outs.
      if (
        submitter &&
        (submitter.hasAttribute('data-no-drawer') ||
          submitter.disabled ||
          submitter.getAttribute('aria-disabled') === 'true' ||
          submitter.classList.contains('disabled'))
      ) {
        return;
      }
      if (form.hasAttribute('data-no-drawer')) return;

      var id = (idField.value || '').trim();
      if (!id || id === 'not-selected') {
        // Variant not chosen: stop navigation and prompt, don't add.
        event.preventDefault();
        event.stopImmediatePropagation();
        showFormError(
          form,
          getDrawerMessage('selectOptionsError', 'Please select the available options first.')
        );
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      addFromForm(form, submitter);
    },
    true
  );

  // Open the drawer from the header cart icons.
  document.addEventListener('click', function (event) {
    if (!OPEN_TRIGGER_SELECTOR) return;
    var trigger = event.target.closest(OPEN_TRIGGER_SELECTOR);
    if (!trigger || !getDrawer()) return;
    event.preventDefault();
    openDrawer(trigger);
  });

  // Drawer internal clicks (overlay, close, qty, remove) — delegated so they
  // survive re-renders.
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-tmg-cd-overlay]') || event.target.closest('[data-tmg-cd-close]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    var qtyBtn = event.target.closest('[data-tmg-cd-qty]');
    if (qtyBtn) {
      event.preventDefault();
      handleQtyStep(qtyBtn);
      return;
    }

    var removeBtn = event.target.closest('[data-tmg-cd-remove]');
    if (removeBtn) {
      event.preventDefault();
      handleRemove(removeBtn);
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-tmg-cd-qty-input]');
    if (input) handleQtyInput(input);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var drawer = getDrawer();
    if (drawer && drawer.classList.contains('is-active')) closeDrawer();
  });

  window.TMGCartDrawer = {
    open: openDrawer,
    close: closeDrawer,
    addFromForm: addFromForm,
    afterExternalAdd: afterExternalAdd,
    showFormError: showFormError,
    /* The bundle builders set data-no-drawer and post their own items, so they
       never reach addFromForm. Exposed so they can reuse the same busy state
       rather than restating the class contract. */
    setButtonLoading: setButtonLoading,
    refresh: function () { return fetchDrawerSection().then(applySection); }
  };

  // Replay whatever landed while this script was still loading. The form is
  // re-submitted rather than added directly, so it goes through the handler
  // above and picks up all of its guards (disabled button, unselected variant,
  // data-no-drawer) instead of duplicating them here.
  if (window.TMGCartPendingForm) {
    var pending = window.TMGCartPendingForm;
    window.TMGCartPendingForm = null;

    if (pending.form.requestSubmit) {
      pending.form.requestSubmit(pending.submitter);
    } else {
      pending.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }
  } else if (window.TMGCartPending) {
    openDrawer(window.TMGCartPending);
    window.TMGCartPending = null;
  }
})();
